import { supabase } from './supabase'; // Assuming supabase client is in utils

export interface MappedMediaOutlet {
  name: string | null;
  domain: string | null;
  mediaType: string | null;
  visitors: number | null;
  logoUrl: string | null;
}

let mediaOutletMap: Map<string, MappedMediaOutlet> | null = null;
let isFetchingMap = false;
let fetchMapPromise: Promise<void> | null = null;

function normalizeSource(source: string): string {
  if (!source) return '';
  try {
    const url = new URL(source.startsWith('http') ? source : `http://${source}`);
    let hostname = url.hostname.toLowerCase();
    // Remove common www/m prefixes for a base domain version
    if (hostname.startsWith('www.')) {
      hostname = hostname.substring(4);
    } else if (hostname.startsWith('m.')) {
      hostname = hostname.substring(2);
    }
    return hostname;
  } catch (e) {
    // If not a valid URL, treat the source as a name and normalize
    return source.toLowerCase().trim();
  }
}

export async function initializeMediaOutletMap(): Promise<void> {
  if (mediaOutletMap) return;
  if (isFetchingMap && fetchMapPromise) return fetchMapPromise;

  isFetchingMap = true;
  fetchMapPromise = (async () => {
    try {
      const { data, error } = await supabase
        .from('media_outlets')
        .select('name, domain, media_type, visitors, logo_url');

      if (error) {
        console.error('Error fetching media outlets for map:', error);
        mediaOutletMap = new Map(); // Initialize empty map on error
        return;
      }

      const newMap = new Map<string, MappedMediaOutlet>();
      if (data) {
        data.forEach(outlet => {
          const mappedOutlet: MappedMediaOutlet = {
            name: outlet.name,
            domain: outlet.domain,
            mediaType: outlet.media_type,
            visitors: outlet.visitors,
            logoUrl: outlet.logo_url,
          };

          // Add variations to the map
          if (outlet.name) {
            newMap.set(outlet.name.toLowerCase().trim(), mappedOutlet);
          }
          if (outlet.domain) {
            const domain = outlet.domain.toLowerCase().trim();
            newMap.set(domain, mappedOutlet);
            newMap.set(`www.${domain}`, mappedOutlet);
            newMap.set(`m.${domain}`, mappedOutlet);
            // Store the base domain if it had a prefix
            if (domain.startsWith('www.')) {
                 newMap.set(domain.substring(4), mappedOutlet);
            } else if (domain.startsWith('m.')) {
                 newMap.set(domain.substring(2), mappedOutlet);
            }
          }
        });
      }
      mediaOutletMap = newMap;
      console.log('Media outlet map initialized with', mediaOutletMap.size, 'entries.');
    } catch (err) {
      console.error('Exception during media outlet map initialization:', err);
      mediaOutletMap = new Map(); // Initialize empty map on error
    } finally {
      isFetchingMap = false;
      fetchMapPromise = null;
    }
  })();
  return fetchMapPromise;
}

export async function getMappedMediaOutlet(
  source: string | null | undefined
): Promise<MappedMediaOutlet | null> {
  if (!source) return null;
  if (!mediaOutletMap) {
    await initializeMediaOutletMap();
  }
  if (!mediaOutletMap) return null; // Should not happen if await is effective

  const normalized = normalizeSource(source);
  let foundOutlet = mediaOutletMap.get(normalized);

  // If not found with direct normalized (which might be a base domain),
  // try with potential prefixes if the source itself was a direct domain.
  if (!foundOutlet) {
    const lowerSource = source.toLowerCase().trim();
    if (lowerSource.includes('.')) { // Simple check if it might be a domain
        foundOutlet = mediaOutletMap.get(lowerSource); // Try original lowercase
        if (!foundOutlet && !lowerSource.startsWith('www.') && !lowerSource.startsWith('m.')) {
            foundOutlet = mediaOutletMap.get(`www.${lowerSource}`) || mediaOutletMap.get(`m.${lowerSource}`);
        }
    }
  }
   // Last attempt: try to match against the 'name' field directly if it wasn't a URL-like source
  if (!foundOutlet) {
    foundOutlet = mediaOutletMap.get(source.toLowerCase().trim());
  }


  return foundOutlet || null;
}

// Function to get media type multipliers (example, adjust as needed)
export function getMediaTypeMultiplier(mediaType: string | null | undefined): number {
  if (!mediaType) return 0.1; // Default or unknown
  switch (mediaType.toLowerCase()) {
    case 'national tv': return 1.0;
    case 'local tv': return 0.8;
    case 'national radio': return 0.7;
    case 'local radio': return 0.5;
    case 'national newspaper': return 1.0;
    case 'local newspaper': return 0.6;
    case 'online': return 0.9; // General online
    case 'major online': return 1.0; // If you have a way to distinguish major online
    case 'blog': return 0.3;
    case 'news agency': return 0.8;
    default: return 0.2;
  }
}

// Function to get visibility multipliers (example, adjust as needed)
export function getVisibilityMultiplier(visibility: string | null | undefined): number {
  if (!visibility) return 1.0; // Default if no visibility specified
  switch (visibility.toLowerCase()) {
    case 'main role': return 1.0;
    case 'mention': return 0.5;
    case 'minor role': return 0.3;
    // Add other visibility types and their multipliers
    default: return 0.2;
  }
}

// Example usage:
// async function test() {
//   await initializeMediaOutletMap();
//   const outlet = await getMappedMediaOutlet("www.cyprus-mail.com");
//   if (outlet) {
//     console.log("Found outlet:", outlet.name, outlet.mediaType, outlet.visitors);
//     console.log("Multiplier:", getMediaTypeMultiplier(outlet.mediaType));
//   } else {
//     console.log("Outlet not found for www.cyprus-mail.com");
//   }
//   const outlet2 = await getMappedMediaOutlet("SigmaLive");
//    if (outlet2) {
//     console.log("Found outlet2:", outlet2.name, outlet2.mediaType, outlet2.visitors);
//   } else {
//     console.log("Outlet not found for SigmaLive");
//   }
// }
// test(); 